import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { NotificationProvider } from '@/context/NotificationContext';
import { BrandProvider } from '@/context/BrandContext';
import { UpsellModal } from '@/components/UpsellModal';
import { Toaster } from '@/components/ui/sonner';
import axios from 'axios';
import { deveEncerrarPor401, sessaoVencida } from '@/lib/sessao';
import { PlanGate } from '@/components/PlanGate';
import { avisarAprendi } from '@/lib/cerebro';
import { destinoDoBrandBrain, destinoDoPlanejador } from '@/lib/abasNoEndereco';
import { ChipAprendi } from '@/components/ChipAprendi';

// ── Rede de Revendas (PROMPT 215) ────────
const RedeDeRevendasPage = lazy(() => import(/* webpackChunkName: "chunk-rede" */ '@/pages/RedeDeRevendasPage'));
const PortalRevendaPage = lazy(() => import(/* webpackChunkName: "chunk-rede" */ '@/pages/PortalRevendaPage'));
const LinksRastreaveisPage = lazy(() => import(/* webpackChunkName: "chunk-trafego" */ '@/pages/LinksRastreaveisPage'));
const CartaDeVendasPage = lazy(() => import(/* webpackChunkName: "chunk-rede" */ '@/pages/CartaDeVendasPage'));
const ConviteRevendaPage = lazy(() => import(/* webpackChunkName: "chunk-rede" */ '@/pages/ConviteRevendaPage'));

// ── chunk-auth ────────────────────────────────────────────────────────────────
const LoginPage        = lazy(() => import(/* webpackChunkName: "chunk-auth" */ '@/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const ResetPasswordPage = lazy(() => import(/* webpackChunkName: "chunk-auth" */ '@/pages/ResetPasswordPage'));

// ── chunk-dashboard ───────────────────────────────────────────────────────────
const RelatorioPublicoPage = lazy(() => import(/* webpackChunkName: "chunk-relatorio-publico" */ '@/pages/RelatorioPublicoPage'));
const DashboardPage = lazy(() => import(/* webpackChunkName: "chunk-dashboard" */ '@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const SettingsPage  = lazy(() => import(/* webpackChunkName: "chunk-dashboard" */ '@/pages/SettingsPage'));
const ReportsPage   = lazy(() => import(/* webpackChunkName: "chunk-dashboard" */ '@/pages/ReportsPage').then(m => ({ default: m.ReportsPage })));

// ── chunk-kanban ──────────────────────────────────────────────────────────────
const TasksPage = lazy(() => import(/* webpackChunkName: "chunk-kanban" */ '@/pages/TasksPage').then(m => ({ default: m.TasksPage })));

// ── chunk-estrategista ────────────────────────────────────────────────────────
const PlanningPage   = lazy(() => import(/* webpackChunkName: "chunk-estrategista" */ '@/pages/PlanningPage').then(m => ({ default: m.PlanningPage })));
// PROMPT 191: o tráfego pago novo (a entrada e a jornada). A aba "Campanha Meta" saiu; as campanhas ficam em /campaigns.
const TrafegoPage    = lazy(() => import(/* webpackChunkName: "chunk-trafego" */ '@/pages/trafego').then(m => ({ default: m.TrafegoPage })));
const JornadaPage    = lazy(() => import(/* webpackChunkName: "chunk-trafego" */ '@/pages/trafego').then(m => ({ default: m.JornadaPage })));
const CampanhasPage  = lazy(() => import(/* webpackChunkName: "chunk-trafego" */ '@/pages/trafego').then(m => ({ default: m.CampanhasPage })));
const CampanhaPage   = lazy(() => import(/* webpackChunkName: "chunk-trafego" */ '@/pages/trafego').then(m => ({ default: m.CampanhaDetalhada })));
// PROMPT 193: a volumetria de criativos (o motor de copy, os templates, a grade).
const VolumetriaPage = lazy(() => import(/* webpackChunkName: "chunk-trafego" */ '@/pages/trafego').then(m => ({ default: m.VolumetriaPage })));

// ── chunk-sdr ─────────────────────────────────────────────────────────────────
const SDRPage              = lazy(() => import(/* webpackChunkName: "chunk-sdr" */ '@/pages/sdr').then(m => ({ default: m.SDRPage })));
const SDRAgendaPage        = lazy(() => import(/* webpackChunkName: "chunk-sdr" */ '@/pages/sdr').then(m => ({ default: m.SDRAgendaPage })));
const SDRClientsPage       = lazy(() => import(/* webpackChunkName: "chunk-sdr" */ '@/pages/sdr').then(m => ({ default: m.SDRClientsPage })));
const WhatsAppBusinessPage = lazy(() => import(/* webpackChunkName: "chunk-sdr" */ '@/pages/WhatsAppBusinessPage').then(m => ({ default: m.WhatsAppBusinessPage })));
// No chunk-sdr de propósito: divide o CreateCampaignDialog e o AudienceSelector
// com a Base de Clientes, e chunk próprio duplicaria os dois no bundle.
const EmailMktPage         = lazy(() => import(/* webpackChunkName: "chunk-sdr" */ '@/pages/email').then(m => ({ default: m.EmailMktPage })));

// ── chunk-jornadas ────────────────────────────────────────────────────────────
// Chunk PRÓPRIO, e não o chunk-sdr: o editor traz a React Flow junto (~64 kB gz
// com o CSS). No chunk-sdr, quem abre a Base de Clientes pagaria por ela sem
// nunca ver o canvas. A lista de jornadas fica na EmailMktPage e não usa a lib.
const JourneyEditorPage    = lazy(() => import(/* webpackChunkName: "chunk-jornadas" */ '@/pages/jornadas').then(m => ({ default: m.JourneyEditorPage })));

// ── chunk-criativo ────────────────────────────────────────────────────────────
const BrandKitPage      = lazy(() => import(/* webpackChunkName: "chunk-criativo" */ '@/pages/BrandKitPage').then(m => ({ default: m.BrandKitPage })));
const SocialPage        = lazy(() => import(/* webpackChunkName: "chunk-criativo" */ '@/pages/SocialPage').then(m => ({ default: m.SocialPage })));
const JobsPage          = lazy(() => import(/* webpackChunkName: "chunk-criativo" */ '@/pages/JobsPage').then(m => ({ default: m.JobsPage })));

// ── chunk-admin ───────────────────────────────────────────────────────────────
const AdminPage          = lazy(() => import(/* webpackChunkName: "chunk-admin" */ '@/pages/AdminPage').then(m => ({ default: m.AdminPage })));
const BackofficePage     = lazy(() => import(/* webpackChunkName: "chunk-admin" */ '@/pages/BackofficePage').then(m => ({ default: m.BackofficePage })));
const GuiaIntegracoesPage = lazy(() => import(/* webpackChunkName: "chunk-admin" */ '@/pages/GuiaIntegracoesPage'));

// ── chunk-billing ─────────────────────────────────────────────────────────────
const BillingSuccessPage = lazy(() => import(/* webpackChunkName: "chunk-billing" */ '@/pages/BillingSuccessPage'));
const BillingCancelPage  = lazy(() => import(/* webpackChunkName: "chunk-billing" */ '@/pages/BillingCancelPage'));

// ── chunk-legal ───────────────────────────────────────────────────────────────
const TermosPage       = lazy(() => import(/* webpackChunkName: "chunk-legal" */ '@/pages/TermosPage'));
const PrivacidadePage  = lazy(() => import(/* webpackChunkName: "chunk-legal" */ '@/pages/PrivacidadePage'));
const ExclusaoDadosPage = lazy(() => import(/* webpackChunkName: "chunk-legal" */ '@/pages/ExclusaoDadosPage'));

// ── Spinner de fallback ───────────────────────────────────────────────────────
const PageSpinner = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f0f0f' }}>
    <div style={{ width: 32, height: 32, border: '3px solid #333', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// TERCEIRO EIXO: a marca ativa viaja em TODA chamada de axios, no header
// X-Brand-Id. O backend resolve com a dependencia de brand_scope.py, que valida
// contra a conta do token e devolve None se o id nao for dela.
//
// Interceptor e nao parametro por chamada porque sao dezenas de chamadas em
// dezenas de arquivos: converter uma a uma garante que alguma fique de fora, e
// a que ficar falha em SILENCIO, operando na conta em vez da marca.
//
// So injeta quando existe. Conta sem marca nenhuma, e usuario que ainda nao
// escolheu, seguem sem o header — e o backend trata isso como "a conta", que e
// o comportamento de hoje.
//
// FICAM DE FORA, porque nao passam pelo axios: as chamadas via fetch() do
// NotificationContext. As do SlideshowModal tambem, e ali esta CERTO — sao
// endpoints de card do Kanban, cuja marca e a do card e nao a da tela.
axios.interceptors.request.use((config) => {
  const marca = localStorage.getItem('activeBrandId');
  if (marca) {
    config.headers = config.headers || {};
    config.headers['X-Brand-Id'] = marca;
  }
  return config;
});

// Global axios interceptor — 402 → dispara evento de saldo insuficiente
axios.interceptors.response.use(
  // O "Aprendi" (PROMPT 206, B3): qualquer resposta que traga uma regra nova abre o chip, em qualquer tela.
  response => { avisarAprendi(response?.data?.aprendi); return response; },
  error => {
    if (error.response?.status === 402) {
      const data = error.response.data;
      // FastAPI wraps detail in {"detail": ...} — extract the inner object
      const payload = data?.detail && typeof data.detail === 'object' ? data.detail : (data || {});
      window.dispatchEvent(new CustomEvent('saldo_insuficiente', { detail: payload }));
    }
    // 401 → SESSAO VENCIDA, desde 10/09/2026. Ate ali este interceptor so tratava
    // 402: nenhuma requisicao com token morto levava ninguem ao login, e a tela
    // ficava "logada" com tudo falhando. As guardas que impedem expulsar quem nao
    // devia — senha errada no login, requisicao sem token, token de antes de um
    // novo login — estao em `deveEncerrarPor401`, e cada uma esta explicada la.
    if (deveEncerrarPor401(error)) sessaoVencida('axios');
    // 403 de acesso (PROMPT 201): a conta foi suspensa ou venceu com a tela aberta; o PlanGate relê o estado.
    if (error.response?.status === 403
      && ['acesso_vencido', 'conta_suspensa', 'subscription_required'].includes(error.response?.data?.detail)) {
      window.dispatchEvent(new CustomEvent('acesso_mudou'));
    }
    return Promise.reject(error);
  }
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-accent border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" />;
  // O usuário da revenda só tem o portal: as telas da plataforma nem abrem (a recusa de verdade é do backend).
  if (user.role === 'revenda') return <Navigate to="/portal-revenda" replace />;
  return <PlanGate>{children}</PlanGate>;
};

// Bloqueia módulos "Em breve" via URL direta: redireciona ao Kanban com o modal.
const ComingSoonGuard = ({ module }) => <Navigate to={`/tasks?comingSoon=${module}`} replace />;

/** Rota que exige um papel, e não apenas estar logado.
 *
 * POR QUE ELA EXISTE. Até 08/09/2026 `/backoffice` e `/admin` usavam só o
 * `PrivateRoute`, que confere login e mais nada. Quem digitasse o endereço na
 * barra abria a TELA — as chamadas de API davam 403, então não vazava dado, mas
 * o cliente via a existência e a forma do Backoffice da Arkipel.
 *
 * A recusa continua sendo do backend; isto evita mostrar a casca de uma tela
 * que não é de quem está olhando.
 */
const RoleRoute = ({ children, papeis }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-accent border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  // Para o Dashboard, e não para o login: quem chegou aqui está autenticado, e
  // mandá-lo à tela de entrada faria parecer que a sessão caiu.
  if (!papeis.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <PlanGate>{children}</PlanGate>;
};

// Keepalive: chama /api/health a cada 4 min enquanto usuário estiver logado
function ServerKeepalive() {
  const { user } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!user) return;
    const ping = () => fetch(`${API}/api/health`).catch(() => {});
    ping(); // ping imediato ao logar
    const id = setInterval(ping, 4 * 60 * 1000); // a cada 4 minutos
    return () => clearInterval(id);
  }, [user, API]);

  return null;
}

// Os endereços antigos do Planejador levam à aba certa, inclusive os links e avisos já mandados (PROMPT 211, G1).
function ParaOPlanejamento() {
  const { pathname, search } = useLocation();
  return <Navigate to={destinoDoPlanejador(pathname, search)} replace />;
}

// /brand-kit, /aprendi e /assets levam ao Brand Brain (PROMPT 211, G2).
function ParaOBrandBrain({ aba }) {
  const { search } = useLocation();
  return <Navigate to={destinoDoBrandBrain(search, aba)} replace />;
}

function AppContent() {
  return (
    <BrowserRouter>
      <ServerKeepalive />
      <Suspense fallback={<PageSpinner />}>
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
        <Route path="/tasks" element={<PrivateRoute><TasksPage /></PrivateRoute>} />
        <Route path="/tasks/ecommerce" element={<PrivateRoute><ComingSoonGuard module="ecommerce" /></PrivateRoute>} />
        <Route path="/tasks/blog" element={<PrivateRoute><ComingSoonGuard module="blog" /></PrivateRoute>} />
        <Route path="/tasks/:moduleFilter" element={<PrivateRoute><TasksPage /></PrivateRoute>} />
        <Route path="/tasks/:moduleFilter/:subFilter" element={<PrivateRoute><TasksPage /></PrivateRoute>} />
        <Route path="/planning" element={<PrivateRoute><PlanningPage /></PrivateRoute>} />
        <Route path="/brand-brain" element={<PrivateRoute><BrandKitPage /></PrivateRoute>} />
        <Route path="/brand-kit" element={<ParaOBrandBrain />} />
        <Route path="/social" element={<PrivateRoute><SocialPage /></PrivateRoute>} />
        <Route path="/sdr" element={<PrivateRoute><SDRPage /></PrivateRoute>} />
        <Route path="/sdr/agenda" element={<PrivateRoute><SDRAgendaPage /></PrivateRoute>} />
        <Route path="/sdr/clientes" element={<PrivateRoute><SDRClientsPage /></PrivateRoute>} />
        <Route path="/whatsapp-business" element={<PrivateRoute><WhatsAppBusinessPage /></PrivateRoute>} />
        <Route path="/email/campanhas" element={<PrivateRoute><EmailMktPage /></PrivateRoute>} />
        <Route path="/email/jornadas/:id" element={<PrivateRoute><JourneyEditorPage /></PrivateRoute>} />
        <Route path="/ads" element={<PrivateRoute><TrafegoPage /></PrivateRoute>} />
        <Route path="/links-rastreaveis" element={<PrivateRoute><LinksRastreaveisPage /></PrivateRoute>} />
        <Route path="/ads/nova" element={<PrivateRoute><JornadaPage /></PrivateRoute>} />
        <Route path="/ads/nova/:id" element={<PrivateRoute><JornadaPage /></PrivateRoute>} />
        <Route path="/ads/volumetria" element={<PrivateRoute><VolumetriaPage /></PrivateRoute>} />
        <Route path="/ads/volumetria/:id" element={<PrivateRoute><VolumetriaPage /></PrivateRoute>} />
        <Route path="/campaigns" element={<PrivateRoute><CampanhasPage /></PrivateRoute>} />
        <Route path="/campaigns/:canal/:id" element={<PrivateRoute><CampanhaPage /></PrivateRoute>} />
        {/* PROMPT 191, T3: as URLs antigas do tráfego pago levam às novas. */}
        <Route path="/campanhas" element={<Navigate to="/campaigns" replace />} />
        <Route path="/ads/campanhas-meta" element={<Navigate to="/campaigns?canal=meta" replace />} />
        <Route path="/trafego" element={<Navigate to="/ads" replace />} />
        <Route path="/trafego/campanhas" element={<Navigate to="/campaigns" replace />} />
        <Route path="/print" element={<PrivateRoute><ComingSoonGuard module="print" /></PrivateRoute>} />
        <Route path="/publications" element={<PrivateRoute><JobsPage /></PrivateRoute>} />
        <Route path="/jobs" element={<Navigate to="/publications" />} />
        <Route path="/admin" element={<RoleRoute papeis={['admin', 'owner', 'superadmin']}><AdminPage /></RoleRoute>} />
        <Route path="/backoffice" element={<RoleRoute papeis={['superadmin']}><BackofficePage /></RoleRoute>} />
        <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
        <Route path="/relatorios" element={<PrivateRoute><ReportsPage /></PrivateRoute>} />
        <Route path="/planejador/*" element={<ParaOPlanejamento />} />
        <Route path="/relatorio-compartilhado/:token" element={<RelatorioPublicoPage />} />
        <Route path="/rede-revendas" element={<PrivateRoute><RedeDeRevendasPage /></PrivateRoute>} />
        <Route path="/rede-revendas/ver-como/:revendaId" element={<PrivateRoute><PortalRevendaPage /></PrivateRoute>} />
        <Route path="/portal-revenda" element={<RoleRoute papeis={['revenda']}><PortalRevendaPage /></RoleRoute>} />
        <Route path="/carta/:token" element={<CartaDeVendasPage />} />
        <Route path="/convite-revenda/:token" element={<ConviteRevendaPage />} />
        <Route path="/aprendi" element={<ParaOBrandBrain aba="aprendi" />} />
        <Route path="/guia-integracoes" element={<PrivateRoute><GuiaIntegracoesPage /></PrivateRoute>} />
        <Route path="/billing/success" element={<BillingSuccessPage />} />
        <Route path="/billing/cancel" element={<BillingCancelPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/termos" element={<TermosPage />} />
        <Route path="/privacidade" element={<PrivacidadePage />} />
        <Route path="/exclusao-de-dados" element={<ExclusaoDadosPage />} />
        <Route path="/calendar" element={<Navigate to="/planning?aba=calendario" replace />} />
        <Route path="/kanban" element={<Navigate to="/tasks" />} />
        <Route path="/assets" element={<ParaOBrandBrain aba="assets" />} />
        <Route path="/" element={<Navigate to="/tasks" />} />

        {/* CURINGA, e ele fica por ULTIMO: o React Router casa a rota mais
            especifica, mas `path="*"` pega tudo, entao qualquer coisa depois
            dele seria inalcancavel.

            POR QUE EXISTE. Sem ele, uma URL que nao casa com nada renderiza
            NADA — o <Routes> nao produz elemento, e como o menu e o cabecalho
            vivem dentro de cada pagina, a tela sai completamente vazia. Quem
            ve nao tem como saber se errou o endereco ou se a plataforma caiu.

            Foi o que aconteceu com /billing em 08/09/2026: um botao no menu de
            perfil apontava para uma rota que nunca existiu, desde 02/03, e
            produzia tela morta em vez de qualquer sinal.

            LEVA AO KANBAN, e nao a uma tela de erro: o destino do "/" ja e
            /tasks, entao endereco desconhecido e endereco vazio terminam no
            mesmo lugar, que e o que o usuario esperaria. `replace` para o
            endereco quebrado nao ficar no historico do navegador. */}
        <Route path="*" element={<Navigate to="/tasks" replace />} />
      </Routes>
      </Suspense>
      <Toaster />
      <UpsellModal />
      <ChipAprendi />
    </BrowserRouter>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        {/* LOTE 204 (S1): as notificações dentro da marca, para reconectar quando a marca ativa muda. */}
        <BrandProvider>
          <NotificationProvider>
            <AppContent />
          </NotificationProvider>
        </BrandProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
